import { useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import {
  SEALED_NOTE_MAX,
  SealedNoteError,
  MAILBOX_DOWN_COPY,
  openSealedNote,
  sealNote,
  type OpenedNote,
  type SealedNote,
} from '../sealedNote';

/**
 * The two little sealed-note flows inside Table talk (friend of the family). Both are tiny
 * dedicated UIs rather than Handoff's built-in dialog: the built-in one is about syncing and
 * sharing app data (QR codes, "only send this to yourself"), and a note passed across the
 * table wants one sentence of instructions and the words, big.
 */

export function SealNoteForm({
  authorName,
  onSealed,
  onCancel,
}: {
  authorName: string;
  /** Called once the note is in its mailbox — the caller posts the announcement line. */
  onSealed: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sealed, setSealed] = useState<SealedNote | null>(null);
  const [copied, setCopied] = useState(false);

  if (sealed) {
    return (
      <div className="flex flex-col gap-3" data-testid="sealed-note-code">
        <p className="text-sm text-fg-2">
          Sealed. 🔒 These are the magic words — say them out loud or text them. Don’t type them
          into the chat: anyone watching this game can read that.
        </p>
        <p className="rounded-md border border-line-strong bg-raised px-4 py-3 text-center font-display text-lg leading-snug text-fg">
          {sealed.spoken}
        </p>
        <p className="text-xs text-fg-3">
          The note self-destructs in 42 hours. We can’t show these words again.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(sealed.spoken)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
            className="h-11 flex-1 rounded-full border border-line text-sm font-semibold text-fg-2"
          >
            {copied ? 'Copied' : 'Copy the words'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-11 flex-1 rounded-full bg-inverse text-sm font-semibold text-inverse-fg"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const note = await sealNote(text, authorName);
      setSealed(note);
      onSealed();
    } catch (caught) {
      setError(caught instanceof SealedNoteError ? caught.message : MAILBOX_DOWN_COPY);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      data-testid="sealed-note-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-fg-2">
        Write something only they should read. It goes in a locked box; you get the magic words to
        open it.
      </p>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={SEALED_NOTE_MAX}
        rows={3}
        placeholder="The secret ingredient is…"
        className="rounded-xl border border-line bg-surface px-3 py-2 text-base text-fg"
        data-testid="sealed-note-input"
      />
      {error && (
        <p className="text-sm text-fg" role="status" data-testid="sealed-note-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-11 flex-1 rounded-full border border-line text-sm font-semibold text-fg-2"
        >
          Never mind
        </button>
        <button
          type="submit"
          disabled={busy || !text.trim()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-inverse text-sm font-semibold text-inverse-fg disabled:opacity-40"
          data-testid="sealed-note-seal"
        >
          <Lock size={15} aria-hidden="true" />
          {busy ? 'Sealing…' : 'Seal it'}
        </button>
      </div>
    </form>
  );
}

export function OpenNoteForm({ onClose }: { onClose: () => void }): JSX.Element {
  const [words, setWords] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<OpenedNote | null>(null);

  if (note) {
    return (
      <div className="flex flex-col gap-3" data-testid="sealed-note-opened">
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-3">From {note.from}</p>
        {/* Plain text, always — the note came from whoever holds the words. */}
        <p className="whitespace-pre-wrap break-words rounded-md border border-line bg-raised px-4 py-3 text-base text-fg">
          {note.text}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="h-11 rounded-full bg-inverse text-sm font-semibold text-inverse-fg"
        >
          Got it 🤫
        </button>
      </div>
    );
  }

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setNote(await openSealedNote(words));
    } catch (caught) {
      setError(caught instanceof SealedNoteError ? caught.message : MAILBOX_DOWN_COPY);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      data-testid="open-note-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-fg-2">Type the magic words they told you.</p>
      <input
        value={words}
        onChange={(event) => setWords(event.target.value)}
        maxLength={200}
        autoCapitalize="none"
        autoComplete="off"
        placeholder="the gentle raven doth wake the silver moon"
        className="h-12 rounded-full border border-line bg-surface px-4 text-base text-fg"
        data-testid="open-note-input"
      />
      {error && (
        <p className="text-sm text-fg" role="status" data-testid="open-note-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-11 flex-1 rounded-full border border-line text-sm font-semibold text-fg-2"
        >
          Never mind
        </button>
        <button
          type="submit"
          disabled={busy || !words.trim()}
          className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-inverse text-sm font-semibold text-inverse-fg disabled:opacity-40"
          data-testid="open-note-submit"
        >
          <LockOpen size={15} aria-hidden="true" />
          {busy ? 'Unsealing…' : 'Open it'}
        </button>
      </div>
    </form>
  );
}
